import { useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  TextField, Box, Button, FormControl, InputLabel, 
  Select, MenuItem, Grid, Typography, Alert,
  FormHelperText
} from '@mui/material';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EventCategory, EventStatus } from '../../types';
import { generateSlug } from '../../utils/slugGenerator';

// Slug removed from schema — auto-generated
const eventSchema = z.object({
  slug: z.string().optional(), // Hidden, auto-generated
  title: z.string().min(1, 'Укажите название события'),
  description: z.string().optional(),
  shortDescription: z.string().optional(),
  eventDate: z.string().refine(date => {
    const selectedDate = new Date(date);
    return selectedDate <= new Date();
  }, {
    message: 'Дата события не может быть в будущем',
  }),
  eventTime: z.string().optional(),
  endDate: z.string().optional(),
  location: z.string().optional(),
  address: z.string().optional(),
  templeId: z.string().optional().nullable(),
  category: z.enum(Object.values(EventCategory) as [string, ...string[]]),
  status: z.enum(Object.values(EventStatus) as [string, ...string[]]),
});

type EventFormValues = z.infer<typeof eventSchema>;

interface EventFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EventFormValues) => Promise<void>;
  initialData?: EventFormValues | null;
  loading: boolean;
  error?: string | null;
}

export function EventForm({ open, onClose, onSubmit, initialData, loading, error }: EventFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EventFormValues>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      slug: '',
      title: '',
      description: '',
      shortDescription: '',
      eventDate: new Date().toISOString().split('T')[0],
      eventTime: '09:00',
      endDate: '',
      location: '',
      address: '',
      templeId: '',
      category: EventCategory.OTHER,
      status: EventStatus.DRAFT,
    },
  });

  const titleValue = watch('title');

  // Auto-generate slug when title changes (only for new items)
  useEffect(() => {
    if (!initialData && titleValue && titleValue.trim().length > 0) {
      const autoSlug = generateSlug(titleValue);
      setValue('slug', autoSlug);
    }
  }, [titleValue, initialData, setValue]);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    } else {
      reset({
        slug: '',
        title: '',
        description: '',
        shortDescription: '',
        eventDate: new Date().toISOString().split('T')[0],
        eventTime: '09:00',
        endDate: '',
        location: '',
        address: '',
        templeId: '',
        category: EventCategory.OTHER,
        status: EventStatus.DRAFT,
      });
    }
  }, [initialData, open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialData ? 'Редактировать событие' : 'Создать событие'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {/* Title — main field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Название события *"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            
            {/* Slug — hidden field, shown as read-only hint */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="URL (автоматически)"
                value={watch('slug') || ''}
                disabled
                helperText="Генерируется из названия"
                sx={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    bgcolor: 'action.hover',
                  }
                }}
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Категория *</InputLabel>
                <Select
                  label="Категория *"
                  {...register('category')}
                >
                  <MenuItem value={EventCategory.LITURGY}>Богослужение</MenuItem>
                  <MenuItem value={EventCategory.MEETING}>Встреча</MenuItem>
                  <MenuItem value={EventCategory.FEAST}>Праздник</MenuItem>
                  <MenuItem value={EventCategory.EDUCATION}>Образовательное</MenuItem>
                  <MenuItem value={EventCategory.CHARITY}>Благотворительность</MenuItem>
                  <MenuItem value={EventCategory.OTHER}>Другое</MenuItem>
                </Select>
                <FormHelperText>{errors.category?.message}</FormHelperText>
              </FormControl>
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Статус *</InputLabel>
                <Select
                  label="Статус *"
                  {...register('status')}
                >
                  <MenuItem value={EventStatus.DRAFT}>Черновик</MenuItem>
                  <MenuItem value={EventStatus.PUBLISHED}>Опубликовано</MenuItem>
                  <MenuItem value={EventStatus.ARCHIVED}>Архив</MenuItem>
                </Select>
                <FormHelperText>{errors.status?.message}</FormHelperText>
              </FormControl>
            </Grid>
            
            {/* Event Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата события *"
                error={!!errors.eventDate}
                helperText={errors.eventDate?.message}
                {...register('eventDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Event Time */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Время события"
                error={!!errors.eventTime}
                helperText={errors.eventTime?.message}
                {...register('eventTime')}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 300, // 5 minute steps
                }}
              />
            </Grid>
            
            {/* End Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата окончания"
                error={!!errors.endDate}
                helperText={errors.endDate?.message}
                {...register('endDate')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Location */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Место проведения"
                error={!!errors.location}
                helperText={errors.location?.message}
                {...register('location')}
              />
            </Grid>
            
            {/* Address */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Адрес"
                error={!!errors.address}
                helperText={errors.address?.message}
                {...register('address')}
              />
            </Grid>
            
            {/* Short Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Краткое описание"
                multiline
                rows={2}
                error={!!errors.shortDescription}
                helperText={errors.shortDescription?.message}
                {...register('shortDescription')}
              />
            </Grid>
            
            {/* Full Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Полное описание"
                multiline
                rows={4}
                {...register('description')}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                * Обязательные поля
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button onClick={handleSubmit(onSubmit)} variant="contained" disabled={loading}>
          {loading ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}